<template>
  <Teleport v-if="show" to="body">
    <div class="maintenance-banner" role="status">
      <span class="dot"></span>
      <span class="text">
        <strong>{{ pack.title }}</strong>
        <span class="muted">— {{ pack.subtitle }}</span>
      </span>
      <NuxtLink to="/settings#maintenance" class="action">
        {{ pack.action }}
      </NuxtLink>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
const { tenant } = useTenantTheme()
const { user, loggedIn } = useUserSession()
const { locale } = useI18n()

const isAdmin = computed(() => {
  if (!loggedIn.value) return false
  const role = (user.value as any)?.role
  return role === 'admin' || role === 'superadmin' || (user.value as any)?.isSuperAdmin === true
})

const show = computed(() =>
  !!tenant.value?.maintenanceMode?.enabled && isAdmin.value
)

const labels = {
  en: {
    title: 'Maintenance mode active',
    subtitle: 'Visitors see a maintenance page. You can navigate because you\'re an admin.',
    action: 'Manage'
  },
  de: {
    title: 'Wartungsmodus aktiv',
    subtitle: 'Besucher sehen eine Wartungsseite. Du kannst die App nutzen, weil du Admin bist.',
    action: 'Verwalten'
  },
  pt: {
    title: 'Modo de manutenção ativo',
    subtitle: 'Os visitantes veem uma página de manutenção. Você pode navegar porque é admin.',
    action: 'Gerenciar'
  },
  es: {
    title: 'Modo de mantenimiento activo',
    subtitle: 'Los visitantes ven una página de mantenimiento. Puedes navegar porque eres administrador.',
    action: 'Gestionar'
  }
} as const

const pack = computed(() => labels[locale.value as keyof typeof labels] || labels.en)
</script>

<style scoped>
.maintenance-banner {
  position: fixed;
  /* Respect the iOS / Android browser-chrome safe area so the pill
     doesn't sit under the address bar / home indicator on mobile. */
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  left: 50%;
  transform: translateX(-50%);
  z-index: 99000;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px 10px 12px;
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(251, 146, 60, 0.12));
  border: 1px solid rgba(251, 191, 36, 0.4);
  border-radius: 999px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(251, 191, 36, 0.1) inset;
  font-size: 0.78rem;
  color: #fff;
  max-width: calc(100vw - 32px);
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from { opacity: 0; transform: translate(-50%, 12px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
}

.dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(251, 191, 36);
  box-shadow: 0 0 10px rgba(251, 191, 36, 0.7);
  animation: pulse 1.8s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.55; transform: scale(0.85); }
}

.text {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.text strong {
  font-weight: 700;
}

.text .muted {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 400;
}

.action {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgb(251, 191, 36);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.2s ease;
}

.action:hover {
  background: rgba(251, 191, 36, 0.18);
  border-color: rgba(251, 191, 36, 0.5);
  color: #fff;
}

@media (max-width: 640px) {
  .maintenance-banner {
    font-size: 0.72rem;
    padding: 8px 12px 8px 10px;
  }
  .text .muted {
    display: none;
  }
}
</style>
